import fs from 'fs/promises'
import path from 'path'
import chalk from 'chalk'

interface GenerateOptions {
  schema: string
  output: string
}

export async function generate(options: GenerateOptions) {
  const { schema, output } = options

  console.log(chalk.blue('📝 Generating TypeScript types...\n'))

  try {
    // Check if schema file exists
    await fs.access(schema)
  } catch {
    throw new Error(`Schema file not found: ${schema}`)
  }

  // Load the schema
  const { loadSchemaFile } = await import('./utils')
  const schemaModule = await loadSchemaFile(schema)
  if (!schemaModule.env) {
    throw new Error(`No 'env' export found in ${schema}`)
  }

  const envSchema = schemaModule.env
  const typeContent: string[] = []
  
  // Add header
  typeContent.push('// Generated TypeScript types from envfortress schema')
  typeContent.push('// This file is auto-generated - do not edit manually')
  typeContent.push('')

  // Generate client interface
  if (envSchema.client && Object.keys(envSchema.client).length > 0) {
    typeContent.push('// Client-side environment variables (available in browser)')
    typeContent.push('export interface ClientEnv {')
    Object.entries(envSchema.client).forEach(([key, schema]) => {
      const type = getTypeFromSchema(schema)
      typeContent.push(`  ${key}: ${type}`)
    })
    typeContent.push('}')
    typeContent.push('')
  }

  // Generate server interface
  if (envSchema.server && Object.keys(envSchema.server).length > 0) {
    typeContent.push('// Server-side environment variables (not available in browser)')
    typeContent.push('export interface ServerEnv {')
    Object.entries(envSchema.server).forEach(([key, schema]) => {
      const type = getTypeFromSchema(schema)
      typeContent.push(`  ${key}: ${type}`)
    })
    typeContent.push('}')
    typeContent.push('')
  }

  // Generate combined interface
  typeContent.push('// Combined environment variables')
  typeContent.push('export interface Env extends ClientEnv, ServerEnv {}')
  typeContent.push('')

  // Generate individual variable types
  typeContent.push('// Individual variable types')
  if (envSchema.client) {
    Object.entries(envSchema.client).forEach(([key, schema]) => {
      const type = getTypeFromSchema(schema)
      typeContent.push(`export type ${key} = ${type}`)
    })
  }
  if (envSchema.server) {
    Object.entries(envSchema.server).forEach(([key, schema]) => {
      const type = getTypeFromSchema(schema)
      typeContent.push(`export type ${key} = ${type}`)
    })
  }

  // Ensure output directory exists
  const outputDir = path.dirname(output)
  await fs.mkdir(outputDir, { recursive: true })

  // Write the file
  await fs.writeFile(output, typeContent.join('\n'), 'utf-8')

  console.log(chalk.green(`✅ Created ${output}`))
  console.log(chalk.blue(`\n📝 Usage:`))
  console.log(chalk.gray(`import type { ClientEnv, ServerEnv, Env } from './${path.relative(process.cwd(), output)}'`))
}

function getTypeFromSchema(schema: any): string {
  if (!schema || !schema._def) return 'unknown';
  const type = schema._def.type;

  switch (type) {
    case 'string':
      return 'string';
    case 'number':
      return 'number';
    case 'boolean':
      return 'boolean';
    case 'enum': {
      const entries = schema._def.entries;
      const values = Object.values(entries).filter(v => typeof v === 'string');
      return values.map((v: string) => `"${v}"`).join(' | ');
    }
    case 'union':
      return schema._def.options.map(getTypeFromSchema).join(' | ');
    case 'array':
      return getTypeFromSchema(schema._def.type) + '[]';
    case 'object':
      return 'Record<string, unknown>';
    case 'optional':
      return `${getTypeFromSchema(schema._def.innerType)} | undefined`;
    case 'default':
      return getTypeFromSchema(schema._def.innerType);
    case 'effects': // refinements like .url(), .email(), .transform(), etc.
      return getTypeFromSchema(schema._def.schema);
    default:
      return 'unknown';
  }
} 